import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Command } from 'cmdk';
import {
    IconSearch, IconLoader2, IconChevronRight,
    IconFileText, IconPhoto, IconPhotoEdit, IconTemplate, IconSparkles, IconPrinter, IconChecklist,
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useDebouncedValue } from '@/lib/hooks';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useModuleUsageStore, rankedModuleIds } from '../../store/moduleUsageStore';
import { useAiTemplateCacheStore } from '../../store/aiTemplateCacheStore';
import generatedDocService from '../../services/generatedDocService';
import templateSetService from '../../services/templateSetService';
import imageGenerationService from '../../services/imageGenerationService';
import printTemplateService from '../../services/printTemplateService';
import aiTemplateService from '../../services/aiTemplateService';
import bulkPhotoService from '../../services/bulkPhotoService';
import questionPaperService from '../../services/questionPaperService';
import { sanitizeText } from '../../utils/sanitize';
import { toBnDigits } from '../../utils/bnFormat';
import ModuleSquircleIcon from './ModuleSquircleIcon';
import { SEARCH_MODULES, SUGGESTED_COUNT, moduleForPath, isModuleAvailable } from './globalSearchModules';

// Global search palette (Ctrl/Cmd + K, or the header search button).
//
//   ┌ search box ──────────────────────────────────────────────────────────────┐
//   │ row 1  modules — a tile row: the five this user opens most when nothing is typed,
//   │                  the matching modules while typing
//   │ row 2  results — a list, only while typing: images (AI photos, manual edits, template images), bulk photo
//   │                  projects, documents, question papers, print media, AI templates, document templates
//   └ keyboard hints ──────────────────────────────────────────────────────────┘
//
// The palette keeps one fixed height from the moment it opens, so it never jumps while results come and go (the list scrolls).

const MAX_QUERY = 100;
const MAX_RELATED_MODULES = 10;

// "doc:" / "img:" at the start of the query narrow the results to one kind (a power-user shortcut; not advertised in the UI).
const FILTERS = [{ prefix: 'doc:', type: 'doc' }, { prefix: 'img:', type: 'img' }];

function parseQuery(q) {
    const raw = q.trim();
    const lower = raw.toLowerCase();
    for (const f of FILTERS) {
        if (lower.startsWith(f.prefix)) return { filterType: f.type, search: raw.slice(f.prefix.length).trim() };
    }
    return { filterType: null, search: raw };
}

const norm = (s) => String(s ?? '').normalize('NFC').toLowerCase();
const tokensOf = (s) => norm(s).split(/\s+/).filter(Boolean);

// AI templates are searched client-side over the shared 12-hour list the header mega menu also uses, so Bengali names
// match as well and a search costs no request once the list is cached.
async function loadAiTemplates() {
    const cache = useAiTemplateCacheStore.getState();
    const fresh = cache.getFreshTemplates('');
    if (fresh !== undefined) return fresh;
    const res = await aiTemplateService.list({ search: '' });
    const list = res?.data || [];
    cache.setTemplates('', list);
    return list;
}

// My Files keeps AI photos, manual edits, AI-template images and Studio Editor photos in separate tabs (they are separate tables,
// and the default feed holds only the first), so each one is searched and the newest matches across all four are kept.
const IMAGE_SOURCES = ['regular', 'manual', 'ai-templates', 'studio'];

async function searchImages({ limit, ...params }) {
    const settled = await Promise.allSettled(
        IMAGE_SOURCES.map((source) => imageGenerationService.getHistory({ page: 1, limit, source, ...params }))
    );
    return settled
        .flatMap((r) => (r.status === 'fulfilled' && r.value?.success ? r.value.data?.history || [] : []))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
}

// An AI-template image only knows its template's id: take the slug from the shared template list, then by id, else the gallery.
async function templatePath(img) {
    if (img.aiTemplateSlug) return `/ai-templates/${img.aiTemplateSlug}`;
    if (!img.aiTemplateId) return '/ai-templates';
    try {
        const list = await loadAiTemplates();
        let slug = list.find((tpl) => String(tpl.id) === String(img.aiTemplateId))?.slug;
        if (!slug) {
            const res = await aiTemplateService.get(img.aiTemplateId);
            slug = res?.data?.slug || res?.slug;
        }
        return slug ? `/ai-templates/${slug}` : '/ai-templates';
    } catch {
        return '/ai-templates';
    }
}

// Where an image result opens — the same places My Files sends it: an AI photo → the AI photo editor, a manual edit → the
// manual editor (with its saved settings), an AI-template image → that template's page.
function imageTarget(img) {
    if (img.manuallyEdited === true) {
        return {
            to: '/generate-image-manual',
            state: {
                historyItem: {
                    id: img.id,
                    aiImage: img.aiImage,
                    background: img.background,
                    size: img.size || img.photoSize,
                    zoom: img.zoom ?? null,
                    offsetX: img.offsetX ?? null,
                    offsetY: img.offsetY ?? null,
                    manualBgRemoved: img.manualBgRemoved ?? false,
                    fileName: img.fileName,
                },
            },
        };
    }
    if (img.aiTemplateGeneration === true) return { resolve: () => templatePath(img) };
    // A Studio Editor photo reopens in the Studio Editor with every setting of the edit.
    if (img.studioEdited === true) return { to: `/studio-editor?editId=${img.id}` };
    return { to: `/generate-image?editId=${img.id}` };
}

// Bulk projects and question papers are filtered by the server (`search`); checking the answer here as well keeps the
// results right even against a server that predates that option (it would hand back the newest ones unfiltered).
const matchesSearch = (search, ...fields) => {
    const hay = norm(fields.join(' '));
    return tokensOf(search).every((tk) => hay.includes(tk));
};

// Icon + tint of each result kind (images, print media and AI templates show their own thumbnail when they have one).
const KIND = {
    image:    { Icon: IconPhoto,    tint: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' },
    bulk:     { Icon: IconPhotoEdit, tint: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' },
    doc:      { Icon: IconFileText, tint: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
    question: { Icon: IconChecklist, tint: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' },
    print:    { Icon: IconPrinter,  tint: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400' },
    ai:       { Icon: IconSparkles, tint: 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-900/20 dark:text-fuchsia-400' },
    template: { Icon: IconTemplate, tint: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400' },
};

// Result groups, in the order they are listed.
const GROUP_HEADING_KEY = {
    images: 'imageGeneration.myImages',
    bulk: 'navigation.bulkPhotoEdit',
    docs: 'globalSearch.documents',
    questions: 'navigation.questionPapers',
    print: 'navigation.printMedia',
    ai: 'navigation.aiTemplates',
    templates: 'globalSearch.templates',
};

const GROUP_HEADING_CLS =
    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-zinc-400 dark:[&_[cmdk-group-heading]]:text-zinc-500';

// A result's thumbnail; falls back to the kind's icon when there is none or it fails to load.
function ResultThumb({ item }) {
    const { Icon, tint } = KIND[item.kind];
    const [failed, setFailed] = useState(false);
    return (
        <span className={`flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded-md ${tint}`}>
            {item.thumb && !failed ? (
                <img
                    src={item.thumb}
                    alt=""
                    loading="lazy"
                    draggable="false"
                    className="h-full w-full object-cover"
                    onError={() => setFailed(true)}
                />
            ) : (
                <Icon size={16} stroke={1.7} />
            )}
        </span>
    );
}

function Kbd({ children }) {
    return (
        <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-zinc-200 bg-white px-1 text-[10px] text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
            {children}
        </kbd>
    );
}

export default function GlobalSearch() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const { isAuthenticated, user } = useAuthStore();
    const officeSpaceId = useOfficeSpaceStore((s) => s.selectedOfficeSpace?.id);
    const userId = user?.id;

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery] = useDebouncedValue(query, 300);
    const [groups, setGroups] = useState([]);       // [{ key, items }] — only while something is typed
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState('');       // the highlighted row/tile (controlled, so the tile row can move it in 2D)
    const [usingPointer, setUsingPointer] = useState(true);   // module tiles show a focus ring only once the keyboard is used — hovering never tints them
    const reqId = useRef(0);
    const rootRef = useRef(null);

    // Ctrl+K / Cmd+K to open the palette, plus a custom event so UI buttons
    // (e.g. the header search button) can open it without a keyboard shortcut.
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (isAuthenticated) setOpen(prev => !prev);
            }
        };
        const openHandler = () => { if (isAuthenticated) setOpen(true); };
        window.addEventListener('keydown', handler);
        window.addEventListener('global-search:open', openHandler);
        return () => {
            window.removeEventListener('keydown', handler);
            window.removeEventListener('global-search:open', openHandler);
        };
    }, [isAuthenticated]);

    // Reset everything when the dialog closes
    const handleOpenChange = useCallback((value) => {
        setOpen(value);
        if (!value) {
            setQuery('');
            setGroups([]);
            setActive('');
            setUsingPointer(true);
        }
    }, []);

    // target: a path, { to, state }, or { resolve } (a path that needs a lookup first)
    const go = useCallback(async (target) => {
        const dest = typeof target === 'string' ? { to: target } : target;
        const to = dest.resolve ? await dest.resolve() : dest.to;
        navigate(to, dest.state ? { state: dest.state } : undefined);
        handleOpenChange(false);
    }, [navigate, handleOpenChange]);

    // ── Learn which modules this user opens (browser-local), to suggest the five used most ──
    const ensureUsage = useModuleUsageStore((s) => s.ensure);
    const bumpUsage = useModuleUsageStore((s) => s.bump);
    const usage = useModuleUsageStore((s) => (userId != null ? s.byUser[userId] : undefined));
    const lastModule = useRef(null);
    useEffect(() => { if (isAuthenticated) ensureUsage(userId); }, [isAuthenticated, userId, ensureUsage]);
    useEffect(() => {
        if (!isAuthenticated || userId == null) { lastModule.current = null; return; }
        const id = moduleForPath(pathname);
        if (id && id !== lastModule.current) bumpUsage(userId, id);   // one visit per entry into a module, not per page inside it
        lastModule.current = id;
    }, [pathname, isAuthenticated, userId, bumpUsage]);

    // ── Modules (instant, no request) ───────────────────────────────────
    const isBdUser = !user?.country || String(user.country).toUpperCase() === 'BD';
    const isOwner = user?.role === 'OWNER';
    const available = useMemo(
        () => SEARCH_MODULES.filter((m) => isModuleAvailable(m, { isBdUser, isOwner })),
        [isBdUser, isOwner]
    );
    // Before anything is typed: the modules used most, topped up with the home-page defaults until there are enough.
    const suggested = useMemo(() => {
        const picked = rankedModuleIds(usage)
            .map((id) => available.find((m) => m.id === id && m.rank !== false))
            .filter(Boolean)
            .slice(0, SUGGESTED_COUNT);
        for (const m of available) {
            if (picked.length >= SUGGESTED_COUNT) break;
            if (m.suggested && !picked.includes(m)) picked.push(m);
        }
        return picked;
    }, [available, usage]);

    const { filterType: typedFilter, search: typedSearch } = parseQuery(query);
    const typing = typedSearch.length > 0 || typedFilter !== null;
    const modules = useMemo(() => {
        if (!typing) return suggested;
        if (typedFilter) return [];                       // "doc:" / "img:" = the user wants files, not modules
        const tokens = tokensOf(typedSearch);
        return available
            .map((m) => {
                const label = norm(t(m.labelKey));
                const hay = `${label} ${norm(m.keywords)}`;
                return tokens.every((tk) => hay.includes(tk)) ? { m, byName: tokens.every((tk) => label.includes(tk)) } : null;
            })
            .filter(Boolean)
            .sort((a, b) => Number(b.byName) - Number(a.byName))        // name matches first, keyword matches after
            .slice(0, MAX_RELATED_MODULES)
            .map((x) => x.m);
    }, [typing, suggested, typedFilter, typedSearch, available, t]);

    // ── Results: images, documents, print media, AI templates, document templates ────────
    useEffect(() => {
        if (!isAuthenticated || !open) return;

        const { filterType, search: rawSearch } = parseQuery(debouncedQuery);
        const search = sanitizeText(rawSearch, { maxLen: MAX_QUERY });
        if (search.length === 0 && filterType === null) {       // nothing typed → modules only, no lists, no requests
            reqId.current += 1;
            setGroups([]);
            setLoading(false);
            return;
        }
        const wantImgs = filterType === null || filterType === 'img';
        const wantBulk = wantImgs;                                   // bulk-edited photos are images too
        const wantDocs = filterType === null || filterType === 'doc';
        const wantQuestions = wantDocs && isBdUser;                  // question papers are Bangladesh-only, like in My Files
        const wantPrint = filterType === null && !!officeSpaceId;
        const wantAi = filterType === null;
        const limit = filterType !== null ? 8 : 4;
        const found = search ? { search } : {};
        const inShop = officeSpaceId ? { officeSpaceId } : {};     // with a shop selected My Files lists the whole shop's files, so search the same
        const myReq = ++reqId.current;

        const fmtDate = (iso) => {
            const d = new Date(iso);
            return Number.isNaN(d.getTime())
                ? ''
                : d.toLocaleDateString(isBn ? 'bn-BD' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        };

        (async () => {
            setLoading(true);
            try {
                const [imgsRes, bulkRes, docsRes, qpRes, printRes, aiRes, tplRes] = await Promise.allSettled([
                    wantImgs ? searchImages({ limit, ...inShop, ...found }) : null,
                    wantBulk ? bulkPhotoService.list({ limit, ...inShop, ...found }) : null,
                    wantDocs ? generatedDocService.getGeneratedDocs({ page: 1, limit, ...inShop, ...found }) : null,
                    wantQuestions ? questionPaperService.list({ page: 1, limit, ...inShop, ...found }) : null,
                    wantPrint ? printTemplateService.listGenerated({ officeSpaceId, limit, ...found }) : null,
                    wantAi ? loadAiTemplates() : null,
                    wantDocs ? templateSetService.getAllTemplateSets({ page: 1, limit, ...found }) : null,
                ]);
                if (myReq !== reqId.current) return;      // a newer search is already running — drop this answer

                const next = [];

                if (imgsRes.status === 'fulfilled' && imgsRes.value) {
                    const items = imgsRes.value.map((img) => ({
                        id: `img-${img.id}`,
                        to: imageTarget(img),
                        title: img.fileName || t('imageGeneration.untitled'),
                        subtitle: fmtDate(img.timestamp),
                        thumb: img.aiImage || '',
                        kind: 'image',
                    }));
                    if (items.length) next.push({ key: 'images', items });
                }

                if (bulkRes.status === 'fulfilled') {
                    const num = (n) => (isBn ? toBnDigits(n) : String(n));
                    const items = (bulkRes.value?.data || []).filter((p) => !search || matchesSearch(search, p.name)).slice(0, limit).map((p) => {
                        const cover = p.images?.[0];                  // same cover photo as the My Files card
                        const unfinished = p.status !== 'completed'
                            ? t(`bulkPhotoEdit.status.${p.status === 'paused' ? 'paused' : 'running'}`, { done: num(p.doneCount), total: num(p.photoCount) })
                            : null;
                        return {
                            id: `bulk-${p.id}`,
                            to: `/bulk-photo-edit/${p.id}`,
                            title: p.name || t('imageGeneration.untitled'),
                            subtitle: [t('bulkPhotoEdit.photoMeta', { count: num(p.photoCount) }), unfinished, fmtDate(p.createdAt)].filter(Boolean).join(' · '),
                            thumb: cover?.enhancedUrl || cover?.originalUrl || '',
                            kind: 'bulk',
                        };
                    });
                    if (items.length) next.push({ key: 'bulk', items });
                }

                if (docsRes.status === 'fulfilled' && docsRes.value?.success) {
                    const items = (docsRes.value.data || []).slice(0, limit).map((d) => {
                        const tmplName = d.templateSet?.name;
                        return {
                            id: `doc-${d.id}`,
                            to: `/documents/regenerate/${d.templateSetId}?groupId=${d.id}`,
                            title: d.title || tmplName || t('imageGeneration.untitled'),
                            subtitle: [tmplName && tmplName !== d.title ? tmplName : null, fmtDate(d.createdAt)].filter(Boolean).join(' · '),
                            kind: 'doc',
                        };
                    });
                    if (items.length) next.push({ key: 'docs', items });
                }

                if (qpRes.status === 'fulfilled') {
                    const items = (qpRes.value?.data || []).filter((p) => !search || matchesSearch(search, p.title, p.settings?.institution)).slice(0, limit).map((p) => ({
                        id: `qp-${p.id}`,
                        to: `/questions-create/${p.id}`,
                        title: p.title || t('imageGeneration.untitled'),
                        subtitle: [(p.settings?.institution || '').trim() || null, fmtDate(p.updatedAt)].filter(Boolean).join(' · '),
                        kind: 'question',
                    }));
                    if (items.length) next.push({ key: 'questions', items });
                }

                if (printRes.status === 'fulfilled') {
                    const items = (printRes.value?.data?.items || []).slice(0, limit).map((it) => ({
                        id: `print-${it.id}`,
                        // opens the editor on the template it was made from; if that is unknown, the My Files list
                        to: it.templateId ? `/print-media/editor/${it.templateId}` : '/documents?tab=printMedia',
                        title: it.title || t('imageGeneration.untitled'),
                        subtitle: [fmtDate(it.createdAt), it.width && it.height ? `${it.width}×${it.height}` : null].filter(Boolean).join(' · '),
                        thumb: it.thumbnailUrl || it.pngUrl || '',
                        kind: 'print',
                    }));
                    if (items.length) next.push({ key: 'print', items });
                }

                if (aiRes.status === 'fulfilled' && Array.isArray(aiRes.value) && search) {
                    const tokens = tokensOf(search);
                    const items = aiRes.value
                        .filter((tpl) => tpl.slug && tokens.every((tk) => norm(`${tpl.name} ${tpl.nameBn} ${tpl.slug}`).includes(tk)))
                        .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
                        .slice(0, limit)
                        .map((tpl) => {
                            const other = isBn ? tpl.name : tpl.nameBn;     // the other language's name goes in the subtitle
                            const own = isBn && tpl.nameBn ? tpl.nameBn : tpl.name;
                            return {
                                id: `ai-${tpl.id}`,
                                to: `/ai-templates/${tpl.slug}`,
                                title: own,
                                subtitle: other && other !== own ? other : '',
                                thumb: tpl.thumbnail || '',
                                kind: 'ai',
                            };
                        });
                    if (items.length) next.push({ key: 'ai', items });
                }

                if (tplRes.status === 'fulfilled' && tplRes.value?.success) {
                    const items = (tplRes.value.data || []).slice(0, limit).map((tpl) => ({
                        id: `tpl-${tpl.id}`,
                        to: `/template-sets/generate/${tpl.slug}`,
                        title: tpl.name,
                        subtitle: tpl.description?.slice(0, 70) || t('dashboard.uncategorized'),
                        kind: 'template',
                    }));
                    if (items.length) next.push({ key: 'templates', items });
                }

                setGroups(next);
            } catch (err) {
                console.error('GlobalSearch error:', err);
                if (myReq === reqId.current) setGroups([]);
            } finally {
                if (myReq === reqId.current) setLoading(false);
            }
        })();
    }, [debouncedQuery, open, isAuthenticated, officeSpaceId, isBdUser, t, isBn]);

    // ── Keyboard: the tile row moves in 2D, everything else is cmdk's up/down ─────────
    const onKeyDown = (e) => {
        setUsingPointer(false);                                              // keyboard in use → the highlighted tile gets its ring
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;          // IME composition (Bangla typing)
        const horizontal = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
        if (!horizontal && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        if (horizontal && query.length > 0) return;                          // keep the text caret usable while typing
        const root = rootRef.current;
        const cur = root?.querySelector('[cmdk-item][data-selected="true"]');
        if (!cur || !cur.hasAttribute('data-tile-id')) return;               // only when a module tile is highlighted

        const cr = cur.getBoundingClientRect();
        const cx = cr.left + cr.width / 2;
        const cy = cr.top + cr.height / 2;
        let best = null;
        let bestScore = Infinity;
        root.querySelectorAll('[data-tile-id]').forEach((el) => {
            if (el === cur) return;
            const r = el.getBoundingClientRect();
            const dx = r.left + r.width / 2 - cx;
            const dy = r.top + r.height / 2 - cy;
            let score = null;
            if (e.key === 'ArrowRight' && dx > 4 && Math.abs(dy) < 8) score = dx;
            else if (e.key === 'ArrowLeft' && dx < -4 && Math.abs(dy) < 8) score = -dx;
            else if (e.key === 'ArrowDown' && dy > 8) score = dy * 1000 + Math.abs(dx);       // nearest row below, then nearest column
            else if (e.key === 'ArrowUp' && dy < -8) score = -dy * 1000 + Math.abs(dx);
            if (score !== null && score < bestScore) { best = el; bestScore = score; }
        });
        if (best) {
            e.preventDefault();
            setActive(best.getAttribute('data-tile-id'));
            best.scrollIntoView({ block: 'nearest' });
        }
        // no tile in that direction → cmdk's default moves on to the next/previous row of the list
    };

    if (!isAuthenticated) return null;

    const shownGroups = typing ? groups : [];
    const hasContent = modules.length > 0 || shownGroups.length > 0;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                showClose={false}
                className="font-hind max-w-[560px] gap-0 overflow-hidden rounded-2xl border border-zinc-200 bg-white p-0 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
                aria-describedby={undefined}
            >
                <DialogTitle className="sr-only">{t('globalSearch.title')}</DialogTitle>
                <Command
                    ref={rootRef}
                    label={t('globalSearch.title')}
                    shouldFilter={false}
                    value={active}
                    onValueChange={setActive}
                    onKeyDown={onKeyDown}
                    onPointerMove={() => setUsingPointer(true)}
                    className="flex h-[min(72vh,420px)] w-full flex-col overflow-hidden rounded-2xl bg-white dark:bg-zinc-900"
                >
                    {/* ── Search box ── */}
                    <div className="flex h-12 items-center gap-3 border-b border-zinc-100 px-4 dark:border-zinc-800">
                        <span className="flex flex-none items-center text-zinc-400">
                            {loading ? <IconLoader2 size={18} className="animate-spin" /> : <IconSearch size={18} />}
                        </span>
                        <Command.Input
                            value={query}
                            onValueChange={setQuery}
                            maxLength={MAX_QUERY}
                            placeholder={t('globalSearch.placeholder')}
                            className="min-w-0 flex-1 border-none bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-zinc-100"
                        />
                        <kbd className="flex-none select-none rounded border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">Esc</kbd>
                    </div>

                    <Command.List className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-1.5">
                        {/* ── Row 1 — modules, as a row of tiles ── */}
                        {modules.length > 0 && (
                            <Command.Group
                                heading={typing ? t('globalSearch.modules') : t('globalSearch.suggestedModules')}
                                className="pt-1.5 [&_[cmdk-group-heading]]:sr-only [&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-5 [&_[cmdk-group-items]]:gap-0.5"
                            >
                                {modules.map((m) => {
                                    const id = `module-${m.id}`.toLowerCase();
                                    return (
                                        <Command.Item
                                            key={m.id}
                                            value={id}
                                            data-tile-id={id}
                                            onSelect={() => go(m.to)}
                                            className={`flex cursor-pointer flex-col items-center gap-1 rounded-xl px-0.5 py-2 text-center ${usingPointer ? '' : 'aria-selected:ring-2 aria-selected:ring-primary/40'}`}
                                        >
                                            <ModuleSquircleIcon src={m.src} Icon={m.Icon} gradient={m.gradient} size={44} className="drop-shadow-md" />
                                            <span className="block min-h-[30px] text-[12px] font-semibold leading-[1.25] text-zinc-700 line-clamp-2 dark:text-zinc-200">
                                                {t(m.labelKey)}
                                            </span>
                                        </Command.Item>
                                    );
                                })}
                            </Command.Group>
                        )}

                        {/* ── Row 2 — images, documents, print media, templates, as a list (only while typing) ── */}
                        {shownGroups.map((group) => (
                            <Command.Group key={group.key} heading={t(GROUP_HEADING_KEY[group.key])} className={GROUP_HEADING_CLS}>
                                {group.items.map((item) => {
                                    return (
                                        <Command.Item
                                            key={item.id}
                                            value={item.id}
                                            onSelect={() => go(item.to)}
                                            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-zinc-50 aria-selected:bg-zinc-100 dark:hover:bg-zinc-800 dark:aria-selected:bg-zinc-800"
                                        >
                                            <ResultThumb item={item} />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[13px] font-semibold leading-snug text-zinc-900 dark:text-zinc-100">{item.title}</span>
                                                {item.subtitle && (
                                                    <span className="block truncate text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">{item.subtitle}</span>
                                                )}
                                            </span>
                                            <IconChevronRight size={14} className="flex-none text-zinc-300 dark:text-zinc-600" />
                                        </Command.Item>
                                    );
                                })}
                            </Command.Group>
                        ))}

                        {/* first load of a list: placeholders instead of an empty area */}
                        {typing && loading && shownGroups.length === 0 && (
                            <div className="space-y-1.5 px-1 pt-2" aria-hidden="true">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="h-11 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800" />
                                ))}
                            </div>
                        )}

                        {typing && !hasContent && !loading && (
                            <Command.Empty className="py-8 text-center text-sm text-zinc-400">
                                {t('common.noResults')}
                            </Command.Empty>
                        )}
                    </Command.List>

                    {/* ── Keyboard hints ── */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-100 bg-zinc-50 px-3.5 py-1.5 text-[11px] text-zinc-400 dark:border-zinc-800 dark:bg-zinc-950/50">
                        <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd>{t('globalSearch.navigate')}</span>
                        <span className="flex items-center gap-1"><Kbd>↵</Kbd>{t('globalSearch.select')}</span>
                        <span className="flex items-center gap-1"><Kbd>Esc</Kbd>{t('globalSearch.close')}</span>
                    </div>
                </Command>
            </DialogContent>
        </Dialog>
    );
}
